import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { MDBox } from '../../components/MDBox';
import { MDTypography } from '../../components/MDTypography';
import { MDButton } from '../../components/MDButton';
import { MDInput } from '../../components/MDInput';
import { useAuthContext } from '../../contexts/authContext';
import { logger } from '../../services/logger';
import {
  automationService,
  ReportAutomation,
  CreateReportAutomationDto,
  QueryConfig,
  FilterConfig,
} from '../../services/automationService';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  stepContent: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  comingSoonBadge: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  emailInput: {
    marginBottom: theme.spacing(2),
  },
  infoAlert: {
    marginBottom: theme.spacing(2),
  },
  stepper: {
    '&.MuiStepper-root': {
      background: 'transparent',
    },
  },
  step: {
    '&.MuiStep-root': {
      color: 'black !important',
    },

    '& .MuiSvgIcon-root': {
      color: 'black !important',
      fill: 'black !important',
      stroke: 'black !important',
      background: 'transparent',
    },

    '& .MuiStepLabel-label': {
      color: 'black !important',
    },

    '& .MuiStepConnector-root': {
      background: 'black !important',
    },
  },
}));

interface ReportAutomationFormProps {
  report?: ReportAutomation | null;
  onSubmit: () => void;
  onCancel: () => void;
}

const steps = [
  'Basic Information',
  'Query Configuration',
  'Schedule & Delivery',
];

export const ReportAutomationForm: React.FC<ReportAutomationFormProps> = ({
  report,
  onSubmit,
  onCancel,
}) => {
  const classes = useStyles();
  const { user } = useAuthContext();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateReportAutomationDto>({
    name: '',
    description: '',
    sourceTable: 'Claims',
    queryConfig: {
      columns: [],
      filters: [],
      orderBy: [],
    },
    frequency: 'daily',
    scheduleHour: 0,
    format: 'CSV',
    deliveryChannel: 'email',
    emailRecipients: [],
    isActive: true,
  });

  const [emailInput, setEmailInput] = useState('');
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);

  useEffect(() => {
    if (report) {
      setFormData({
        name: report.name,
        description: report.description,
        sourceTable: report.sourceTable,
        queryConfig: report.queryConfig,
        frequency: report.frequency,
        scheduleHour: report.scheduleHour,
        scheduleDayOfMonth: report.scheduleDayOfMonth,
        scheduleDayOfWeek: report.scheduleDayOfWeek,
        format: report.format,
        deliveryChannel: report.deliveryChannel,
        emailRecipients: report.emailRecipients,
        sftpConfigId: report.sftpConfigId,
        isActive: report.isActive,
      });
    }
  }, [report]);

  useEffect(() => {
    // Update available columns when source table changes
    const columns = automationService.getTableColumns(formData.sourceTable);
    setAvailableColumns(columns);

    // Reset selected columns if they're not available in the new table
    setFormData((prev) => ({
      ...prev,
      queryConfig: {
        ...prev.queryConfig,
        columns: prev.queryConfig.columns.filter((col) =>
          columns.includes(col)
        ),
      },
    }));
  }, [formData.sourceTable]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (report) {
        await automationService.updateReportAutomation(report.id, formData);
      } else {
        await automationService.createReportAutomation(formData);
      }

      onSubmit();
    } catch (error) {
      logger.error('Failed to save report automation', {
        error: error instanceof Error ? error.message : String(error),
      });
      setError('Failed to save report automation. Please try again.');
    } finally {
      setLoading(false);
      setFormData({
        name: '',
        description: '',
        sourceTable: 'Claims',
        queryConfig: {
          columns: [],
          filters: [],
          orderBy: [],
        },
        frequency: 'daily',
        scheduleHour: 0,
        format: 'CSV',
        deliveryChannel: 'email',
        emailRecipients: [],
        isActive: true,
      });
    }
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim().toLowerCase();

    if (!trimmedEmail) return;

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check if email is in the same domain as the tenant
    const userDomain = user?.email?.split('@')[1];
    const inputDomain = trimmedEmail.split('@')[1];

    if (userDomain && inputDomain !== userDomain) {
      setError(`Email must be in the ${userDomain} domain`);
      return;
    }

    // Check if already added
    if (formData.emailRecipients.includes(trimmedEmail)) {
      setError('This email is already added');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      emailRecipients: [...prev.emailRecipients, trimmedEmail],
    }));
    setEmailInput('');
    setError(null);
  };

  const handleRemoveEmail = (email: string) => {
    setFormData((prev) => ({
      ...prev,
      emailRecipients: prev.emailRecipients.filter((e) => e !== email),
    }));
  };

  const handleColumnToggle = (column: string) => {
    setFormData((prev) => ({
      ...prev,
      queryConfig: {
        ...prev.queryConfig,
        columns: prev.queryConfig.columns.includes(column)
          ? prev.queryConfig.columns.filter((c) => c !== column)
          : [...prev.queryConfig.columns, column],
      },
    }));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Basic Information
        return (
          <Box className={classes.stepContent}>
            <MDBox className={classes.formSection}>
              <MDInput
                label="Report Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                fullWidth
                required
                placeholder="e.g., Daily Claims Report"
              />
            </MDBox>

            <MDBox className={classes.formSection}>
              <MDInput
                label="Description"
                value={formData.description || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                fullWidth
                multiline
                rows={3}
                placeholder="Brief description of what this report contains"
              />
            </MDBox>

            <MDBox className={classes.formSection}>
              <FormControl variant="outlined">
                <InputLabel>Source Table</InputLabel>
                <Select
                  style={{ color: 'black', minWidth: 120, height: 40 }}
                  value={formData.sourceTable}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sourceTable: e.target.value as
                        | 'Claims'
                        | 'ClaimsBills'
                        | 'Steerage',
                    }))
                  }
                  label="Source Table"
                >
                  <MenuItem value="Claims">Claims</MenuItem>
                  <MenuItem value="ClaimsBills">Claims Bills</MenuItem>
                  <MenuItem value="Steerage">Steerage</MenuItem>
                </Select>
                <FormHelperText>
                  Select the data source for your report
                </FormHelperText>
              </FormControl>
            </MDBox>
          </Box>
        );

      case 1: // Query Configuration
        return (
          <Box className={classes.stepContent}>
            <Alert severity="info" className={classes.infoAlert}>
              Select the columns you want to include in your report. You can
              also add filters later.
            </Alert>

            <MDBox className={classes.formSection}>
              <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                Select Columns
              </MDTypography>
              <FormGroup>
                {availableColumns.map((column) => (
                  <FormControlLabel
                    key={column}
                    control={
                      <Checkbox
                        checked={formData.queryConfig.columns.includes(column)}
                        onChange={() => handleColumnToggle(column)}
                      />
                    }
                    label={column}
                  />
                ))}
              </FormGroup>
            </MDBox>

            <MDBox className={classes.formSection}>
              <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                Filters (Optional)
              </MDTypography>
              <MDTypography variant="caption" color="text" paragraph>
                Add filters to narrow down your report data
              </MDTypography>
              <Alert severity="info" icon={<InfoIcon />}>
                Advanced filtering will be available in a future update
              </Alert>
            </MDBox>
          </Box>
        );

      case 2: // Schedule & Delivery
        return (
          <Box className={classes.stepContent}>
            <MDBox className={classes.formSection}>
              <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                Report Format
              </MDTypography>
              <RadioGroup
                value={formData.format}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    format: e.target.value as any,
                  }))
                }
              >
                <FormControlLabel value="CSV" control={<Radio />} label="CSV" />
                <FormControlLabel
                  value="XLSX"
                  control={<Radio />}
                  label="XLSX (coming soon)"
                  disabled
                  className={classes.comingSoonBadge}
                />
                <FormControlLabel
                  value="TXT"
                  control={<Radio />}
                  label="TXT (coming soon)"
                  disabled
                  className={classes.comingSoonBadge}
                />
              </RadioGroup>
            </MDBox>

            <MDBox className={classes.formSection}>
              <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                Delivery Channel
              </MDTypography>
              <RadioGroup
                value={formData.deliveryChannel}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    deliveryChannel: e.target.value as any,
                  }))
                }
              >
                <FormControlLabel
                  value="email"
                  control={<Radio />}
                  label="Email"
                />
                <FormControlLabel
                  value="sftp"
                  control={<Radio />}
                  label="SFTP (coming soon)"
                  disabled
                  className={classes.comingSoonBadge}
                />
              </RadioGroup>
            </MDBox>

            {formData.deliveryChannel === 'email' && (
              <MDBox className={classes.formSection}>
                <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                  Email Recipients
                </MDTypography>
                <MDInput
                  label="Add Email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                  fullWidth
                  className={classes.emailInput}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleAddEmail} size="small">
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={`Emails must be in the ${
                    user?.email?.split('@')[1]
                  } domain`}
                />
                <Box className={classes.chipContainer}>
                  {formData.emailRecipients.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveEmail(email)}
                      size="small"
                    />
                  ))}
                </Box>
              </MDBox>
            )}

            <MDBox className={classes.formSection}>
              <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                Schedule
              </MDTypography>
              <FormControl fullWidth margin="normal">
                <InputLabel>Frequency</InputLabel>
                <Select
                  style={{ color: 'black', minWidth: 120, height: 40 }}
                  value={formData.frequency}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      frequency: e.target.value as any,
                    }))
                  }
                  label="Frequency"
                >
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>

              {formData.frequency !== 'hourly' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Hour (UTC)</InputLabel>
                  <Select
                    style={{ color: 'black', minWidth: 120, height: 40 }}
                    value={formData.scheduleHour || 0}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        scheduleHour: Number(e.target.value),
                      }))
                    }
                    label="Hour (UTC)"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {i.toString().padStart(2, '0')}:00
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {formData.frequency === 'weekly' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Day of Week</InputLabel>
                  <Select
                    style={{ color: 'black', minWidth: 120, height: 40 }}
                    value={formData.scheduleDayOfWeek || 0}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        scheduleDayOfWeek: Number(e.target.value),
                      }))
                    }
                    label="Day of Week"
                  >
                    <MenuItem value={0}>Sunday</MenuItem>
                    <MenuItem value={1}>Monday</MenuItem>
                    <MenuItem value={2}>Tuesday</MenuItem>
                    <MenuItem value={3}>Wednesday</MenuItem>
                    <MenuItem value={4}>Thursday</MenuItem>
                    <MenuItem value={5}>Friday</MenuItem>
                    <MenuItem value={6}>Saturday</MenuItem>
                  </Select>
                </FormControl>
              )}

              {formData.frequency === 'monthly' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Day of Month</InputLabel>
                  <Select
                    style={{ color: 'black', minWidth: 120, height: 40 }}
                    value={formData.scheduleDayOfMonth || 1}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        scheduleDayOfMonth: Number(e.target.value),
                      }))
                    }
                    label="Day of Month"
                  >
                    {Array.from({ length: 28 }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {i + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </MDBox>
          </Box>
        );

      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return formData.name.trim().length > 0;
      case 1:
        return formData.queryConfig.columns.length > 0;
      case 2:
        return formData.deliveryChannel === 'email'
          ? formData.emailRecipients.length > 0
          : true;
      default:
        return false;
    }
  };

  return (
    <Box className={classes.root}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {renderStepContent()}

      <Box className={classes.actionButtons}>
        <MDButton
          color="secondary"
          onClick={activeStep === 0 ? onCancel : handleBack}
        >
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </MDButton>
        <Box>
          {activeStep === steps.length - 1 ? (
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleSubmit}
              disabled={loading || !isStepValid()}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading
                ? 'Saving...'
                : report
                ? 'Update Report'
                : 'Create Report'}
            </MDButton>
          ) : (
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleNext}
              disabled={!isStepValid()}
            >
              Next
            </MDButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};
